import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pin, EyeOff, RefreshCw } from 'lucide-react';
import type { GongFlowSummary, GongFlowBrowseSort } from '@shared/flow/gong-flow-catalog';
import { AUTOFILL_IGNORE_PROPS } from '../../../lib/autofill';
import { useEnrollQueuePanelWidthCss } from './panel-width-store';
import { formatRelativeTime } from './request-format';

/**
 * Gong flow browse surface (U12; promoted to a full-height panel in round-5
 * plan `2026-08-20-001` U3 — QA doc §1). Purely presentational: props in,
 * callbacks out, NO context reads and NO data fetching of its own
 * (architecture note, terra-gong-flow-play-queue-redesign-3lw.12) — it
 * renders in a test with hand-built props and no QueryClientProvider. The
 * connected piece (`useGongFlowBrowseController`, `gong-flow-queries.ts`)
 * owns the search/folder/sort state and the actual `GET /api/flow/gong/flows`
 * fetch (one fetch of the full catalog per browse-open, filtered client-side
 * — round-5 plan `2026-08-20-001`, Decisions #1/#7/#8; there is no pagination
 * anymore, so no `hasNext`/`hasPrev`/`onNext`/`onPrev` props or footer);
 * `PlayPicker.tsx` wires the two together only while the overlay is open.
 *
 * U3 (QA doc §1): the root used to be an `absolute inset-0` overlay sized by
 * whatever container it happened to render inside — a small in-row dropdown
 * on the queue dock, a portaled `w-72` menu on contact cards — which crushed
 * the result list under an uncapped folder-chip wall. It's now a
 * `createPortal(document.body)` fixed panel at the dock's own width
 * (`useEnrollQueuePanelWidthCss()`, `./panel-width-store` — the SAME
 * rep-draggable value the dock itself renders, so it keeps matching the dock
 * exactly, including mid-drag) from BOTH mount sites, `z-[60]` (above the
 * dock's `z-50`, the retired full-screen review dialog's overlay precedent). A
 * portal is DOM placement, not data coupling — this component is still
 * purely presentational. The folder chip row (unbounded — 104 chips against
 * production data) is gone in favor of a native `<select>` (Decision #5);
 * search now sits above it with a folder-aware placeholder (Decision #6).
 * No focus trap — matches the dock's own no-trap posture; Escape closes via
 * a plain `onKeyDown` on the dialog (the `GlobalSearch.tsx`/`Modal.tsx`
 * sibling pattern).
 *
 * Folder-grouped accordion (Spencer, 2026-08-20): against the real catalog
 * (664 flows / 104 folders) even a de-crushed flat list is still a long
 * scroll. With NO active search term, rows are grouped by `folder_name`
 * (`'Unfiled'` for `null`) into collapsed-by-default disclosure sections —
 * the same `<button aria-expanded>` + chevron pattern
 * `StepOverrideEditor.tsx`'s `StepOverrideRow` already uses for its step
 * rows, reused here rather than inventing a second disclosure widget. A
 * non-empty `search` skips grouping entirely and falls back to today's flat
 * relevance-sorted list — search's whole point is a ranked flat result, and
 * folder grouping would fight that ordering (the caller's
 * `filterAndSortGongFlows` already does the ranking; this component never
 * re-filters, it only decides how to lay out whatever `flows` it was given).
 * The folder `<select>` above keeps narrowing the underlying array before
 * grouping — selecting one folder naturally collapses to one section, no
 * special-casing.
 */

const ALL_FOLDERS = null;

/** `folder_name ?? UNFILED_GROUP_LABEL` — the same "Unfiled" fallback each row already renders. */
const UNFILED_GROUP_LABEL = 'Unfiled';

interface GongFlowFolderGroup {
  folder: string;
  flows: GongFlowSummary[];
}

/**
 * Groups an already-filtered/sorted flow list by folder, preserving each
 * folder's first-appearance order in `flows` (which is itself already
 * ordered by the shared `filterAndSortGongFlows` — pinned/recency/name) and
 * each flow's position within its folder. Pure, presentational-only: no
 * re-sorting, no re-filtering.
 */
function groupFlowsByFolder(flows: GongFlowSummary[]): GongFlowFolderGroup[] {
  const order: string[] = [];
  const byFolder = new Map<string, GongFlowSummary[]>();
  for (const flow of flows) {
    const key = flow.folder_name ?? UNFILED_GROUP_LABEL;
    let bucket = byFolder.get(key);
    if (!bucket) {
      bucket = [];
      byFolder.set(key, bucket);
      order.push(key);
    }
    bucket.push(flow);
  }
  return order.map((folder) => ({ folder, flows: byFolder.get(folder) as GongFlowSummary[] }));
}

interface GongFlowRowCallbacks {
  onSelect: (flow: GongFlowSummary) => void;
  onPin: (flow: GongFlowSummary) => void;
  onHide: (flow: GongFlowSummary) => void;
}

interface GongFlowRowProps extends GongFlowRowCallbacks {
  flow: GongFlowSummary;
}

/** One result row — extracted so both the flat (search-active) list and the grouped (no-search) list render identical rows. */
function GongFlowRow({ flow, onSelect, onPin, onHide }: GongFlowRowProps) {
  return (
    <div className="flex items-center justify-between gap-2 px-2 py-1 rounded hover:bg-bg-secondary dark:hover:bg-nightshade-800">
      <button
        type="button"
        aria-label={`Assign to ${flow.name}`}
        onClick={() => onSelect(flow)}
        className="flex-1 min-w-0 text-left"
      >
        <div className="truncate text-[12px] text-content-primary dark:text-nightshade-100">{flow.name}</div>
        <div className="truncate text-[10.5px] text-content-tertiary dark:text-nightshade-500">
          {flow.folder_name ?? UNFILED_GROUP_LABEL} · {flow.step_count} steps
        </div>
      </button>
      <div className="flex-shrink-0 flex flex-col items-end gap-0.5">
        <span className="px-1.5 py-0.5 rounded text-[9.5px] uppercase tracking-wide bg-bg-secondary text-content-secondary dark:bg-nightshade-800 dark:text-nightshade-400">
          {flow.visibility}
        </span>
        <span className="text-[10.5px] whitespace-nowrap text-content-tertiary dark:text-nightshade-500">
          you sent {flow.send_count_30d}× / 30d
          {flow.last_sent_at ? ` · last ${formatRelativeTime(flow.last_sent_at)}` : ''}
        </span>
      </div>
      <button
        type="button"
        aria-label={flow.pinned ? `Unpin ${flow.name}` : `Pin ${flow.name}`}
        title={flow.pinned ? 'Unpin' : 'Pin'}
        onClick={() => onPin(flow)}
        className={flow.pinned ? 'text-content-action dark:text-blueberry-400' : 'text-content-tertiary dark:text-nightshade-500 hover:text-content-action'}
      >
        <Pin className="w-3.5 h-3.5" fill={flow.pinned ? 'currentColor' : 'none'} />
      </button>
      <button
        type="button"
        aria-label={`Hide ${flow.name}`}
        title="Hide"
        onClick={() => onHide(flow)}
        className="text-content-tertiary hover:text-content-danger dark:text-nightshade-500"
      >
        <EyeOff className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

interface GongFlowFolderSectionProps extends GongFlowRowCallbacks {
  group: GongFlowFolderGroup;
  expanded: boolean;
  onToggle: () => void;
}

/**
 * One collapsible folder section — the disclosure pattern
 * `StepOverrideEditor.tsx`'s `StepOverrideRow` uses for step rows
 * (`<button aria-expanded>` + a `▾`/`▸` chevron), reused verbatim rather than
 * a new widget. Collapsed by default (`expanded` is caller-owned state); the
 * button's accessible name is an explicit `aria-label` of "{folder}
 * ({count})" (rather than relying on the visual chevron+text content, which
 * concatenates with no separating whitespace) — `aria-expanded` communicates
 * the toggle state, so no rows mount into the DOM at all while collapsed.
 */
function GongFlowFolderSection({ group, expanded, onToggle, onSelect, onPin, onHide }: GongFlowFolderSectionProps) {
  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={`${group.folder} (${group.flows.length})`}
        className="w-full flex items-center gap-1.5 px-2 py-1 rounded text-left text-[11px] font-medium text-content-secondary dark:text-nightshade-400 hover:bg-bg-secondary dark:hover:bg-nightshade-800"
      >
        <span className="text-content-tertiary dark:text-nightshade-500">{expanded ? '▾' : '▸'}</span>
        <span className="truncate">{group.folder}</span>
        <span className="text-content-tertiary dark:text-nightshade-500 font-normal">({group.flows.length})</span>
      </button>
      {expanded && (
        <div className="pl-1">
          {group.flows.map((flow) => (
            <GongFlowRow key={flow.flow_id} flow={flow} onSelect={onSelect} onPin={onPin} onHide={onHide} />
          ))}
        </div>
      )}
    </div>
  );
}

export interface GongFlowBrowseProps {
  flows: GongFlowSummary[];
  folders: string[];
  stale: boolean;
  isLoading: boolean;
  isError: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  folder: string | null;
  onFolderChange: (folder: string | null) => void;
  sort: GongFlowBrowseSort;
  onSortChange: (sort: GongFlowBrowseSort) => void;
  onPin: (flow: GongFlowSummary) => void;
  onHide: (flow: GongFlowSummary) => void;
  /**
   * Manual refresh escape hatch (R5b/U7) — forces a live re-fetch past the
   * U6 15-minute browse TTL. Rendered as an icon button in the header;
   * `isRefreshing` drives its disabled/spinner state so a double press can't
   * stack requests, and `refreshError` surfaces a non-blocking notice
   * without touching the currently-rendered `flows`.
   */
  onRefresh: () => void;
  isRefreshing: boolean;
  refreshError: boolean;
  /** Selecting a row assigns that flow (caller maps this to `onAssign`). */
  onSelect: (flow: GongFlowSummary) => void;
  onClose: () => void;
  /**
   * The back control's visible label (U3, Decision #4). `QueueBody`'s
   * dock-mounted picker passes `"← Queue"` (this panel takes over the
   * dock's own body while open, so "back" reads correctly); the
   * contact-card portal mount leaves this unset and gets the default
   * `"Close"` (there is no underlying dock surface to go "back" to).
   * Either way the control fires `onClose` — this is a label change only,
   * never a second close mechanism.
   */
  backLabel?: string;
  /**
   * "Assigning {name}" subtitle under the "Gong Flows" title (U3, Decision
   * #4) — threaded from whichever contact this panel is assigning a flow
   * FOR: `contactDisplayIdentity(item)` from `QueueBody`, `contactName`
   * from the contact-card mount via `ContactActionMenus` → `PlayPicker`.
   * Omitted renders no subtitle at all (defensive default; both real
   * mounts pass one).
   */
  contactLabel?: string;
}

function chipClass(active: boolean): string {
  return active
    ? 'px-2 py-0.5 rounded-full text-[10.5px] bg-blueberry-500 text-white'
    : 'px-2 py-0.5 rounded-full text-[10.5px] bg-bg-secondary text-content-secondary dark:bg-nightshade-800 dark:text-nightshade-400 hover:bg-white dark:hover:bg-nightshade-700';
}

export function GongFlowBrowse({
  flows,
  folders,
  stale,
  isLoading,
  isError,
  search,
  onSearchChange,
  folder,
  onFolderChange,
  sort,
  onSortChange,
  onPin,
  onHide,
  onRefresh,
  isRefreshing,
  refreshError,
  onSelect,
  onClose,
  backLabel,
  contactLabel,
}: GongFlowBrowseProps) {
  // U8: the dock's rep-dragged width — the SAME value `RailDock`'s fixed
  // aside and `AppRouter`'s `QueueContentShift` read, so this overlay keeps
  // matching the dock it renders on top of (see `panel-width-store.ts`).
  const panelWidth = useEnrollQueuePanelWidthCss();
  const isSearching = search.trim().length > 0;
  const groups = useMemo(() => (isSearching ? null : groupFlowsByFolder(flows)), [isSearching, flows]);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const toggleFolder = (folderKey: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderKey)) {
        next.delete(folderKey);
      } else {
        next.add(folderKey);
      }
      return next;
    });
  };

  return createPortal(
    <div
      role="dialog"
      aria-label="Browse Gong flows"
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      // Bug fix (round-5 QA follow-up, Codex review #3): this panel is its
      // own `createPortal(document.body)` — a DOM SIBLING of, not a
      // descendant of, whatever ref-tracked container opened it (the
      // Enroll dropdown's own portaled menu on the contact-card mount,
      // `ContactActionMenus.tsx`'s `useDropdown`; the queue dock has no such
      // handler and is unaffected). That host's outside-click listener is a
      // document-level `mousedown` listener that closes on any target
      // outside its own trigger/menu refs — which every interaction inside
      // THIS panel satisfies, since this panel's DOM node was never inside
      // those refs. Stopping propagation here keeps every such mousedown
      // from ever reaching `document`, so this panel stays open regardless
      // of which host opened it (the panel is presentational and does not
      // know about its host's dismissal logic — this is a purely local,
      // reusable containment, not a special case for one caller).
      onMouseDown={(e) => e.stopPropagation()}
      style={{ width: panelWidth }}
      className="fixed top-0 right-0 h-full max-w-full z-[60] flex flex-col bg-white dark:bg-nightshade-900 border-l border-secondary dark:border-nightshade-700 shadow-2xl"
    >
      <div className="flex-shrink-0 px-3 py-2.5 border-b border-secondary dark:border-nightshade-700">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-[11px] text-content-action dark:text-blueberry-400 hover:underline"
          >
            {backLabel ?? 'Close'}
          </button>
          <button
            type="button"
            onClick={onRefresh}
            disabled={isRefreshing}
            aria-label="Refresh Gong flows"
            title={isRefreshing ? 'Refreshing…' : 'Refresh Gong flows'}
            className="flex-shrink-0 flex items-center gap-1 rounded-md border border-content-action bg-content-action px-2 py-1 text-[11px] font-semibold text-white hover:bg-content-action-hover disabled:opacity-60 disabled:cursor-wait dark:border-blueberry-500 dark:bg-blueberry-500"
          >
            <RefreshCw className={`w-3 h-3 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        <div className="mt-1 text-[13px] font-semibold text-content-primary dark:text-nightshade-100">
          Gong Flows
        </div>
        {contactLabel && (
          <div className="text-[10.5px] text-content-tertiary dark:text-nightshade-500">
            Assigning {contactLabel}
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 flex flex-col p-2">
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by flow or folder name…"
          aria-label="Search Gong flows"
          {...AUTOFILL_IGNORE_PROPS}
          className="flex-shrink-0 w-full text-[11px] px-2 py-1 rounded border border-secondary dark:border-nightshade-700 bg-white dark:bg-nightshade-800 text-content-primary dark:text-nightshade-100"
        />

        <select
          value={folder ?? ''}
          onChange={(e) => onFolderChange(e.target.value === '' ? ALL_FOLDERS : e.target.value)}
          aria-label="Filter by folder"
          className="flex-shrink-0 mt-1.5 w-full text-[11px] px-2 py-1 rounded border border-secondary dark:border-nightshade-700 bg-white dark:bg-nightshade-800 text-content-primary dark:text-nightshade-100"
        >
          <option value="">All folders</option>
          {folders.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>

        <div className="flex-shrink-0 mt-1.5 flex items-center gap-1">
          <span className="text-[10px] uppercase tracking-wide text-content-tertiary dark:text-nightshade-500">Sort</span>
          <button type="button" onClick={() => onSortChange('recent')} className={chipClass(sort === 'recent')}>
            Recent
          </button>
          <button type="button" onClick={() => onSortChange('newest')} className={chipClass(sort === 'newest')}>
            Newest
          </button>
        </div>

        {stale && (
          <div
            data-testid="gong-flow-browse-stale-banner"
            className="flex-shrink-0 mt-1.5 px-1.5 py-1 rounded text-[10.5px] bg-bg-orange-light text-content-warning dark:text-amber-400"
          >
            Showing cached flows — Gong is unreachable right now.
          </div>
        )}

        {isError && (
          <div
            role="alert"
            className="flex-shrink-0 mt-1.5 px-1.5 py-1 rounded text-[10.5px] border border-content-danger/30 bg-content-danger/5 text-content-danger"
          >
            Failed to load Gong flows.
          </div>
        )}

        {refreshError && (
          <div
            role="status"
            data-testid="gong-flow-refresh-error"
            className="flex-shrink-0 mt-1.5 px-1.5 py-1 rounded text-[10.5px] border border-content-danger/30 bg-content-danger/5 text-content-danger"
          >
            Refresh failed — showing the previous list.
          </div>
        )}

        <div className="mt-1.5 flex-1 overflow-y-auto">
          {isLoading && flows.length === 0 && (
            <div className="text-[11px] text-content-tertiary dark:text-nightshade-500 px-1 py-1">Loading…</div>
          )}
          {!isLoading && !isError && flows.length === 0 && (
            <div className="flex flex-col items-start gap-1.5 px-1 py-1">
              <div className="text-[11px] text-content-tertiary dark:text-nightshade-500">No Gong flows match.</div>
              <button
                type="button"
                onClick={onRefresh}
                disabled={isRefreshing}
                aria-label="Refresh Gong flows (no results)"
                className="flex items-center gap-1 rounded-md border border-content-action bg-content-action px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-content-action-hover disabled:opacity-60 disabled:cursor-wait dark:border-blueberry-500 dark:bg-blueberry-500"
              >
                <RefreshCw className={`w-3 h-3 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Refreshing…' : 'Refresh Gong flows'}
              </button>
            </div>
          )}
          {groups
            ? groups.map((group) => (
                <GongFlowFolderSection
                  key={group.folder}
                  group={group}
                  expanded={expandedFolders.has(group.folder)}
                  onToggle={() => toggleFolder(group.folder)}
                  onSelect={onSelect}
                  onPin={onPin}
                  onHide={onHide}
                />
              ))
            : flows.map((flow) => (
                <GongFlowRow key={flow.flow_id} flow={flow} onSelect={onSelect} onPin={onPin} onHide={onHide} />
              ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
